import { z } from "zod";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Combine class names with tailwind-merge for proper Tailwind CSS class deduplication
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function generatePassword(length: number = 12): string {
  const uppercaseChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowercaseChars = "abcdefghijklmnopqrstuvwxyz";
  const numberChars = "0123456789";
  const specialChars = "!@#$%^&*()_+[]{}<>?,./";

  // Ensure password has at least one of each type of character
  const allChars = uppercaseChars + lowercaseChars + numberChars + specialChars;

  // Function to get a random character from a given string
  const getRandomChar = (chars: string) =>
    chars[Math.floor(Math.random() * chars.length)];

  // Initialize password with one character from each required set
  const password = [
    getRandomChar(uppercaseChars),
    getRandomChar(lowercaseChars),
    getRandomChar(numberChars),
    getRandomChar(specialChars),
  ];

  // Fill the rest of the password length with random characters
  for (let i = 4; i < length; i++) {
    password.push(getRandomChar(allChars));
  }

  // Shuffle the password to ensure randomness
  return password.sort(() => 0.5 - Math.random()).join("");
}

/**
 * Parse data against a schema and handle validation errors consistently
 * @param schema The validation schema to use
 * @param data The data to validate
 * @returns The parsed and validated data
 * @throws Error with formatted validation issues if parsing fails
 */
export function parseSchema<TSchema extends z.ZodSchema>(
  schema: TSchema,
  data: unknown,
): z.infer<TSchema> {
  const parsedData = schema.safeParse(data);

  if (!parsedData.success) {
    const issuesMessage = parsedData.error.issues.map((issue) => [
      issue.path.join("."),
      issue.message,
    ]);
    throw new Error(
      `Could not parse response: ${JSON.stringify(issuesMessage)}`,
      {
        cause: parsedData.error,
      },
    );
  }

  return parsedData.data;
}

/**
 * Client-side environment variables (VITE_* variables)
 * These are available in both server and client contexts
 */
const clientEnvSchema = z.object({
  VITE_BUILDER_API_KEY: z.string().min(1, "VITE_BUILDER_API_KEY is required"),
  VITE_CLERK_PUBLISHABLE_KEY: z
    .string()
    .min(1, "VITE_CLERK_PUBLISHABLE_KEY is required"),
  VITE_GCP_BUCKET_NAME: z.string().min(1, "VITE_GCP_BUCKET_NAME is required"),
  VITE_APP_HOST: z.string().optional(),
  VITE_APP_URL: z.string().optional(),
  VITE_GHL_COMPANY_ID: z.string().optional(),
  VITE_GHL_LOCATION_ID: z.string().optional(),
  VITE_STRIPE_PUBLISHABLE_KEY: z.string().optional(),
});

/**
 * Parse and validate client environment variables
 * Uses import.meta.env for client-side access
 */
function createClientEnv() {
  const result = clientEnvSchema.safeParse({
    ...process.env,
    ...import.meta.env,
  });

  if (!result.success) {
    const issues = result.error.issues
      .map((issue) => `${issue.path.join(".")}: ${issue.message}`)
      .join("\n");

    throw new Error(`Invalid client environment variables:\n${issues}`);
  }

  return result.data;
}

/**
 * Client-side environment variables
 * Uses import.meta.env for client-side access
 */
export const clientEnv = createClientEnv();

/**
 * Type definitions for environment variables
 */
export type ClientEnv = z.infer<typeof clientEnvSchema>;

export function isNotNull<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function isTuple<T>(array: T[]): array is [T, ...T[]] {
  return array.length > 0;
}

export function nonNull<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function areObjectsEqual<T extends Record<string, unknown>>(
  obj1: T,
  obj2: T,
): boolean {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  return keys1.every((key) => {
    const val1 = obj1[key];
    const val2 = obj2[key];

    if (typeof val1 === "object" && typeof val2 === "object") {
      return areObjectsEqual(
        val1 as Record<string, unknown>,
        val2 as Record<string, unknown>,
      );
    }

    return val1 === val2;
  });
}

export function unproxify<T>(val: T): T {
  if (val instanceof Array) return val.map(unproxify) as T;
  if (val instanceof Object)
    return Object.fromEntries(
      Object.entries(Object.assign({}, val)).map(([k, v]) => [k, unproxify(v)]),
    ) as T;
  return val;
}

/**
 * Extract a cookie value from a cookie string
 * @param cookieString The cookie string from the request header
 * @param name The name of the cookie to extract
 * @returns The cookie value or null if not found
 */
export function getCookieValue(
  cookieString: string,
  name: string,
): string | null {
  const match = cookieString.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
}

/**
 * Get the app URL with runtime hostname preference
 * @param request Optional request object to extract hostname from
 * @returns The app URL (runtime hostname preferred, fallback to VITE_APP_URL)
 */
export function getAppUrl(request?: Request): string {
  if (request) {
    const url = new URL(request.url);
    return `${url.protocol}//${url.host}`;
  }

  // Fallback to environment variable
  const envUrl = process.env.VITE_APP_URL || clientEnv.VITE_APP_URL;
  return envUrl || "http://localhost:5173";
}

// ==========================================
// General-purpose data helpers
// ==========================================

/**
 * Return the first non-empty trimmed string from a list of values.
 */
export function firstNonEmpty(
  ...values: Array<string | null | undefined>
): string {
  for (const v of values) {
    const s = (v ?? "").trim();
    if (s) return s;
  }
  return "";
}

/**
 * Normalize a string or string[] into string[]; return undefined for empty input.
 */
export function toStringArray(
  value: string | string[] | undefined,
): string[] | undefined {
  if (!value) return undefined;
  if (Array.isArray(value)) return value.map((v) => v.trim()).filter(Boolean);
  const single = value.trim();
  return single ? [single] : undefined;
}
